import { useRouter } from "next/router";
import { useState } from "react";
import CustomModal from "~components/CustomModal/CustomModal";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import { FooterActionsOrder } from "../hooks";
import {
  HighDemandModalContent,
  HighDemandModalContentAffectedTicket,
} from "./HighDemandModalContent";
import { LockingInPriceSpinner } from "./LockingInPriceSpinner";

interface LockingInPriceFlowModalProps
  extends LockingInPriceFlowModalContentsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LockingInPriceFlowModal({
  isOpen,
  onClose,
  ...contentsProps
}: LockingInPriceFlowModalProps) {
  return (
    <CustomModal isOpen={isOpen} close={onClose}>
      <LockingInPriceFlowModalContents {...contentsProps} />
    </CustomModal>
  );
}

interface LockingInPriceFlowModalContentsProps {
  selectedTickets: IReleaseTicket[];
  order?: FooterActionsOrder;
  showFees: boolean;
}

export function LockingInPriceFlowModalContents({
  selectedTickets,
  order,
  showFees,
}: LockingInPriceFlowModalContentsProps) {
  const router = useRouter();

  const [highDemandAffectedTickets, setHighDemandAffectedTickets] = useState<
    HighDemandModalContentAffectedTicket[]
  >([]);

  if (highDemandAffectedTickets.length > 0) {
    return (
      <HighDemandModalContent
        affectedTickets={highDemandAffectedTickets}
        order={order}
        showFees={showFees}
      />
    );
  }

  return (
    <LockingInPriceSpinner
      isLoading={!order}
      onAnimationComplete={() => {
        const affectedTickets = findHighDemandAffectedTickets(
          selectedTickets,
          order.lineItems
        );

        if (affectedTickets.length > 0) {
          return setHighDemandAffectedTickets(affectedTickets);
        }

        void router.push("/checkout/[orderId]", `/checkout/${order.id}`);
      }}
    />
  );
}

/**
 * Finds line items for tickets that have a different pricing tier in the order
 * than were shown to the user on the reservation page during ticket selection.
 *
 * NOTE: This function makes the following presumptions:
 * - All selected tickets of the same type have the same pricing tier.
 * - All tickets of the same type will share a single line item, (and will
 *   therefore have the same pricing tier).
 */
function findHighDemandAffectedTickets(
  selectedTickets: IReleaseTicket[],
  orderLineItems: FooterActionsOrder["lineItems"]
): HighDemandModalContentAffectedTicket[] {
  const selectedTicketsPricingTiers: Record<string, string> = {};
  for (const ticket of selectedTickets) {
    if (ticket.pricingTierId) {
      selectedTicketsPricingTiers[ticket.id] = ticket.pricingTierId;
    }
  }

  const highDemandAffectedTickets: HighDemandModalContentAffectedTicket[] = [];
  for (const { node: lineItem } of orderLineItems.edges) {
    const ticketType = lineItem.ticketType;

    if (!ticketType) {
      continue;
    }

    const expectedPricingTierId = selectedTicketsPricingTiers[ticketType.id];
    const actualPricingTierId = lineItem.pricingTierId ?? undefined;

    if (actualPricingTierId === expectedPricingTierId) {
      continue;
    }

    const selectedTicket = selectedTickets.find((t) => t.id === ticketType.id);

    highDemandAffectedTickets.push({
      lineItemId: lineItem.id,
      name: ticketType.name,
      price: lineItem.price,
      fee: lineItem.ticketFeeAmount,
      quantity: lineItem.quantity,
      // Only consider `inHighDemand` because the ticket must be available as
      // it's in the user's cart.
      customBadge: selectedTicket?.inHighDemand
        ? {
            label: selectedTicket.customBadge,
            textColor: selectedTicket.customBadgeTextColor,
            backgroundColor: selectedTicket.customBadgeBackgroundColor,
          }
        : undefined,
    });
  }

  return highDemandAffectedTickets;
}
